<template>
    <div>
        <!-- Page Banner -->
        <PageBanner 
            :pageTitle="blog ? blog.title : 'Blog Details'"
        />
        
        <!-- Blog Details Section -->
        <div class="container ptb-115">
            <!-- Loading State -->
            <div v-if="loading" class="row">
                <div class="col-12 text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="mt-3 text-para">Loading blog post...</p>
                </div>
            </div>
            
            <!-- Error State -->
            <div v-else-if="error" class="row">
                <div class="col-lg-8 mx-auto text-center">
                    <h2 class="section-title style-one fw-semibold mb-20">{{ error }}</h2>
                    <p class="text-para mb-30">The blog post you are looking for could not be found.</p>
                    <router-link to="/blog" class="btn style-one d-inline-flex flex-wrap align-items-center transition">
                        <span class="btn-text d-flex flex-column align-items-center justify-content-center transition">
                            Back to Blog
                        </span>
                        <span class="btn-icon d-flex flex-column align-items-center justify-content-center transition">
                            <i class="ri-arrow-right-line"></i>
                        </span>
                    </router-link>
                </div>
            </div>
            
            <!-- Blog Content -->
            <div v-else-if="blog" class="row">
                <div class="col-xl-8 offset-xl-2">
                    <!-- Featured Image -->
                    <img 
                        v-if="blog.banner_image" 
                        :src="blog.banner_image" 
                        :alt="blog.title" 
                        class="round-20 mb-20 w-100" 
                    />
                    
                    <!-- Category Badge -->
                    <router-link
                        v-if="blog.category"
                        :to="`/blog/category/${blog.category_slug || 'general'}`"
                        class="blog-category fs-14 bg-optional text-white round-oval transition mb-15 d-inline-block"
                    >
                        {{ blog.category }}
                    </router-link>
                    
                    <!-- Title -->
                    <h1 class="section-title style-one mb-25">
                        {{ blog.title }}
                    </h1>
                    
                    <!-- Meta Info -->
                    <ul class="blog-metainfo style-two list-unstyled mb-25">
                        <li v-if="blog.author || blog.author_name" class="blog-author position-relative text_secondary">
                            <span class="d-flex flex-column align-items-center justify-content-center rounded-circle bg-title">
                                <i class="ri-user-line"></i>
                            </span>
                            By:
                            <span class="text_secondary">
                                {{ blog.author || blog.author_name }}
                            </span>
                        </li>
                        <li v-if="blog.published_at || blog.created_at" class="position-relative">
                            <span class="text-para">
                                <i class="ri-calendar-line me-5"></i>
                                {{ formatDate(blog.published_at || blog.created_at) }}
                            </span>
                        </li>
                        <li v-if="blog.views || blog.view_count" class="position-relative">
                            <span class="text-para">
                                <i class="ri-eye-line me-5"></i>
                                {{ blog.views || blog.view_count }} Views
                            </span>
                        </li>
                    </ul>
                    
                    <!-- Blog Content -->
                    <div class="blog-details-content" v-if="blog.content">
                        <div v-html="blog.content" ref="blogContent"></div>
                    </div>
                    
                    <!-- Excerpt if no content -->
                    <div v-else-if="blog.excerpt || blog.summary" class="blog-details-content">
                        <p>{{ blog.excerpt || blog.summary }}</p>
                    </div>
                    
                    <!-- Tags and Share -->
                    <div class="post-metaoption mb-30 mt-40">
                        <div class="row">
                            <div class="col-md-6" v-if="blog.tags && blog.tags.length">
                                <div class="post-tag d-flex fle-wrap align-items-center mb-sm-20">
                                    <span class="fw-semibold text-title me-2">Tags:</span>
                                    <ul class="d-flex align-items-center list-unstyled mb-0">
                                        <li v-for="(tag, index) in blog.tags" :key="index" class="me-1">
                                            <router-link 
                                                :to="`/blog/tag/${tag.slug || tag}`" 
                                                class="text-para hover-text-primary transition"
                                            >
                                                {{ tag.name || tag }}
                                            </router-link>
                                            {{ index < blog.tags.length - 1 ? ',' : '' }}
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="post-share d-flex flex-wrap align-items-center justify-content-md-end">
                                    <span class="text-title fw-semibold me-2">Share:</span>
                                    <ul class="social-profile style-three list-unstyled mb-0">
                                        <li v-for="(social, index) in socialLinks" :key="index">
                                            <a
                                                :href="getSocialShareUrl(social.platform)"
                                                target="_blank"
                                                class="d-flex flex-column align-items-center justify-content-center rounded-circle"
                                            >
                                                <i :class="social.icon"></i>
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Navigation -->
                    <div class="row align-items-center mb-40">
                        <div class="col-md-6 text-md-start text-center mb-15">
                            <router-link
                                v-if="prevBlog"
                                :to="`/blog/${prevBlog.slug}`"
                                class="btn style-two d-inline-flex flex-wrap align-items-center transition"
                            >
                                <span class="btn-icon d-flex flex-column align-items-center justify-content-center transition">
                                    <i class="ri-arrow-left-line"></i>
                                </span>
                                <span class="btn-text fw-semibold d-flex flex-column align-items-center justify-content-center transition">
                                    Prev Post
                                </span>
                            </router-link>
                        </div>
                        <div class="col-md-6 text-md-end text-center mb-15">
                            <router-link
                                v-if="nextBlog"
                                :to="`/blog/${nextBlog.slug}`"
                                class="btn style-two d-inline-flex flex-wrap align-items-center transition"
                            >
                                <span class="btn-text fw-semibold d-flex flex-column align-items-center justify-content-center transition">
                                    Next Post
                                </span>
                                <span class="btn-icon d-flex flex-column align-items-center justify-content-center transition">
                                    <i class="ri-arrow-right-line"></i>
                                </span>
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import PageBanner from '@/components/Common/PageBanner.vue'
import { useSiteDefaultsStore } from '@/stores/siteDefaultsStore'

export default {
    name: 'BlogDetails',
    components: {
        PageBanner
    },
    data() {
        return {
            blog: null,
            prevBlog: null,
            nextBlog: null,
            loading: true,
            error: null,
            socialLinks: [
                { platform: 'facebook', icon: 'ri-facebook-fill' },
                { platform: 'twitter', icon: 'ri-twitter-x-line' },
                { platform: 'linkedin', icon: 'ri-linkedin-fill' },
                { platform: 'whatsapp', icon: 'ri-whatsapp-line' }
            ]
        }
    },
    computed: {
        siteDefaultsStore() {
            return useSiteDefaultsStore()
        }
    },
    async mounted() {
        await this.loadBlogPost()
    },
    watch: {
        '$route'() {
            this.loadBlogPost()
        }
    },
    methods: {
        async loadBlogPost() {
            const slug = this.$route.params.slug
            
            if (!slug) {
                this.error = 'Blog post not found'
                this.loading = false
                return
            }
            
            this.loading = true
            this.error = null
            
            try {
                const response = await this.$api.get(`public/blogs/details/${slug}`)
                
                if (response.data && response.data.status === 'success') {
                    this.blog = response.data.data
                    
                    // Load prev/next posts if available
                    this.prevBlog = response.data.prev || null
                    this.nextBlog = response.data.next || null
                    
                    // Update page meta
                    if (this.blog.meta_title || this.blog.title) {
                        document.title = (this.blog.meta_title || this.blog.title) + ' - Blog'
                    }
                    
                    if (this.blog.meta_description) {
                        const metaDescription = document.querySelector('meta[name="description"]')
                        if (metaDescription) {
                            metaDescription.setAttribute('content', this.blog.meta_description)
                        }
                    }

                    // Enhance galleries after DOM updates
                    this.$nextTick(() => {
                        this.enhanceGalleries()
                    })
                } else {
                    this.error = 'Blog post not found'
                }
            } catch (error) {
                console.error('Failed to load blog post:', error)
                if (error.response && error.response.status === 404) {
                    this.error = 'Blog post not found'
                } else {
                    this.error = 'Failed to load blog post'
                }
            } finally {
                this.loading = false
            }
        },
        
        formatDate(dateString) {
            if (!dateString) return ''
            const date = new Date(dateString)
            const options = { year: 'numeric', month: 'short', day: 'numeric' }
            return date.toLocaleDateString('en-US', options)
        },
        
        getSocialShareUrl(platform) {
            const url = encodeURIComponent(window.location.href)
            const title = encodeURIComponent(this.blog?.title || '')
            
            const shareUrls = {
                facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
                twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
                linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
                whatsapp: `https://api.whatsapp.com/send?text=${title}%20${url}`
            }
            
            return shareUrls[platform] || '#'
        },

        enhanceGalleries() {
            const lightbox = this.$root?.$refs?.galleryLightbox
            if (lightbox && this.$refs.blogContent) {
                lightbox.enhanceGalleries(this.$refs.blogContent)
            }
        }
    }
}
</script>

<style scoped>
.blog-details-content {
    font-size: 16px;
    line-height: 1.8;
    color: #666;
}

.blog-details-content :deep(h1),
.blog-details-content :deep(h2),
.blog-details-content :deep(h3),
.blog-details-content :deep(h4),
.blog-details-content :deep(h5),
.blog-details-content :deep(h6) {
    color: #333;
    font-weight: 600;
    margin-bottom: 20px;
    margin-top: 30px;
}

.blog-details-content :deep(p) {
    margin-bottom: 20px;
}

.blog-details-content :deep(ul),
.blog-details-content :deep(ol) {
    margin-bottom: 20px;
    padding-left: 30px;
}

.blog-details-content :deep(img) {
    max-width: 100%;
    height: auto;
    border-radius: 10px;
    margin: 20px 0;
}

.blog-details-content :deep(blockquote) {
    border-left: 4px solid var(--bs-primary);
    padding-left: 20px;
    margin: 30px 0;
    font-style: italic;
    color: #555;
}

.blog-category {
    padding: 6px 20px;
    text-decoration: none;
}

.blog-category:hover {
    background: var(--bs-primary) !important;
}

.blog-metainfo.style-two {
    display: flex;
    flex-wrap: wrap;
    gap: 25px;
    padding-bottom: 25px;
    border-bottom: 1px solid #e5e5e5;
}

.blog-metainfo li {
    display: flex;
    align-items: center;
    gap: 8px;
}

.blog-author span {
    width: 35px;
    height: 35px;
    font-size: 16px;
}

.post-metaoption {
    padding: 30px 0;
    border-top: 1px solid #e5e5e5;
    border-bottom: 1px solid #e5e5e5;
}

.social-profile.style-three {
    display: flex;
    gap: 10px;
}

.social-profile.style-three li a {
    width: 40px;
    height: 40px;
    background: #f5f5f5;
    transition: all 0.3s ease;
}

.social-profile.style-three li a:hover {
    background: var(--bs-primary);
    color: white;
    transform: translateY(-3px);
}

.social-profile.style-three li a i {
    font-size: 18px;
}

@media (max-width: 767px) {
    .blog-metainfo.style-two {
        gap: 15px;
    }
}
</style>

